import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { HelpCircle, Plus, Edit, Trash2, Search, CheckCircle2, XCircle, FileText, MoreHorizontal, ChevronLeft, ChevronRight, Filter, X } from 'lucide-react';
import { HelpArticle, helpArticleService, HelpArticleFilters } from '@/services/helpArticleService';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

// İstatistik Kartları Bileşeni
function ArticleStats({ statistics }: { statistics: { total: number; active: number; inactive: number } }) {
  const items = [
    {
      label: "Toplam Makale",
      value: statistics.total,
      icon: FileText,
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
    },
    {
      label: "Aktif Makale",
      value: statistics.active,
      icon: CheckCircle2,
      color: "text-green-500",
      bgColor: "bg-green-500/10",
    },
    {
      label: "Pasif Makale",
      value: statistics.inactive,
      icon: XCircle,
      color: "text-red-500",
      bgColor: "bg-red-500/10",
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {items.map((item) => (
        <Card key={item.label} className="overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 hover:border-slate-300 bg-white rounded-xl">
          <CardContent className="p-0">
            <div className="relative">
              <div className={`absolute top-0 right-0 w-32 h-32 opacity-5 transform translate-x-12 -translate-y-12 rounded-full ${item.bgColor} blur-2xl`}></div>
              <div className="p-6 relative">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{item.label}</p>
                    <h3 className="text-3xl font-bold text-slate-900 tracking-tight">{item.value}</h3>
                  </div>
                  <div className={cn("p-3.5 rounded-xl shadow-sm", item.bgColor)}>
                    <item.icon className={cn("w-5 h-5", item.color)} strokeWidth={2.5} />
                  </div>
                </div>
                <div className="pt-3 border-t border-slate-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-xs text-slate-600">
                      <span className={`inline-block w-2 h-2 rounded-full mr-2 ${item.value > 0 ? item.bgColor.replace('/10', '') : "bg-slate-300"}`}></span>
                      <span className="font-medium">
                        {item.label === "Aktif Makale" && `%${statistics.total > 0 ? Math.round((item.value / statistics.total) * 100) : 0} aktif`}
                        {item.label === "Pasif Makale" && `%${statistics.total > 0 ? Math.round((item.value / statistics.total) * 100) : 0} pasif`}
                        {item.label === "Toplam Makale" && "Sistemde kayıtlı"}
                      </span>
                    </div>
                    <span className={cn("text-xs font-semibold", item.color)}>{item.value}/{statistics.total}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function HelpArticleListPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [articles, setArticles] = React.useState<HelpArticle[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [statistics, setStatistics] = React.useState({ total: 0, active: 0, inactive: 0 });
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [articleToDelete, setArticleToDelete] = React.useState<string | null>(null);

  // Sayfalama
  const [currentPage, setCurrentPage] = React.useState(1);
  const itemsPerPage = 10;

  // Filters
  const [filters, setFilters] = React.useState<HelpArticleFilters>({
    page: 1,
    limit: 50,
  });
  const [searchTerm, setSearchTerm] = React.useState('');
  const [filterOpen, setFilterOpen] = React.useState(false);

  // Filtre aktif mi kontrol et
  const isFilterActive = filters.status !== undefined;

  // Fetch articles
  const fetchArticles = React.useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await helpArticleService.getArticles(filters);
      setArticles(response.data);
      setStatistics(response.statistics);
    } catch (error) {
      console.error('Error fetching articles:', error);
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Yardım makaleleri getirilirken bir hata oluştu.',
      });
    } finally {
      setIsLoading(false);
    }
  }, [filters, toast]);

  React.useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  // Search with debounce
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchTerm, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Handle filter changes
  const handleFilterChange = (key: keyof HelpArticleFilters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === 'all' ? undefined : value,
      page: 1,
    }));
  };

  // Handle delete
  const handleDelete = async () => {
    if (!articleToDelete) return;

    try {
      await helpArticleService.deleteArticle(articleToDelete);
      toast({
        title: 'Başarılı',
        description: 'Yardım makalesi başarıyla silindi.',
      });
      fetchArticles();
    } catch (error) {
      console.error('Error deleting article:', error);
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Makale silinirken bir hata oluştu.',
      });
    } finally {
      setDeleteDialogOpen(false);
      setArticleToDelete(null);
    }
  };

  const openDeleteDialog = (id: string) => {
    setArticleToDelete(id);
    setDeleteDialogOpen(true);
  };

  // Sayfalama hesaplamaları
  const totalPages = Math.ceil(articles.length / itemsPerPage);
  const currentArticles = articles.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Arama/filtre değiştiğinde sayfayı sıfırla
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters.status]);

  if (isLoading) {
    return (
      <div className="h-full flex-1">
        <div className="flex-1 space-y-6 p-8 pt-6">
          {/* Hero Section Skeleton */}
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <div
              className="flex"
              style={{
                background: 'linear-gradient(to right, #1e40af, #3b82f6)',
              }}
            >
              <div className="p-8 flex justify-between items-start relative w-full">
                <div className="space-y-3 flex-1">
                  <Skeleton className="h-5 w-40 bg-white/20" />
                  <Skeleton className="h-9 w-64 bg-white/30" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-[500px] bg-white/20" />
                    <Skeleton className="h-4 w-[450px] bg-white/20" />
                  </div>
                </div>
                <Skeleton className="w-40 h-40 rounded-lg bg-white/10 ml-8" />
              </div>
            </div>
          </div>

          {/* Stats Cards Skeleton */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <Card key={i} className="overflow-hidden border border-slate-200 shadow-sm bg-white rounded-xl">
                <CardContent className="p-0">
                  <div className="relative">
                    <div className="absolute top-0 right-0 w-32 h-32 opacity-5 transform translate-x-12 -translate-y-12 rounded-full bg-slate-200 blur-2xl"></div>
                    <div className="p-6 relative">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <Skeleton className="h-4 w-[100px] mb-2" />
                          <Skeleton className="h-9 w-[50px]" />
                        </div>
                        <Skeleton className="h-14 w-14 rounded-xl" />
                      </div>
                      <div className="pt-3 border-t border-slate-100">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Skeleton className="w-2 h-2 rounded-full mr-2" />
                            <Skeleton className="h-4 w-[80px]" />
                          </div>
                          <Skeleton className="h-4 w-[30px]" />
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Filters Skeleton */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="flex gap-3 items-center">
              <Skeleton className="h-11 flex-1" />
              <Skeleton className="h-11 w-28" />
              <Skeleton className="h-11 w-32" />
            </div>
          </div>

          {/* Table Skeleton */}
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <Table className="w-full table-fixed">
              <TableHeader>
                <TableRow className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
                  <TableHead className="py-4 pl-6 w-[280px]">
                    <Skeleton className="h-5 w-[120px]" />
                  </TableHead>
                  <TableHead className="py-4 w-[200px]">
                    <Skeleton className="h-5 w-[100px]" />
                  </TableHead>
                  <TableHead className="py-4 w-[120px]">
                    <Skeleton className="h-5 w-[60px]" />
                  </TableHead>
                  <TableHead className="py-4 w-[80px]">
                    <Skeleton className="h-5 w-[40px]" />
                  </TableHead>
                  <TableHead className="py-4 pr-6 w-[80px] text-right">
                    <Skeleton className="h-5 w-[60px] ml-auto" />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i} className="border-b border-slate-100">
                    <TableCell className="py-4 pl-6">
                      <div className="space-y-1">
                        <Skeleton className="h-5 w-[200px]" />
                        <Skeleton className="h-4 w-[150px]" />
                      </div>
                    </TableCell>
                    <TableCell className="py-4">
                      <Skeleton className="h-5 w-[120px]" />
                    </TableCell>
                    <TableCell className="py-4">
                      <Skeleton className="h-7 w-[60px] rounded-lg" />
                    </TableCell>
                    <TableCell className="py-4">
                      <Skeleton className="h-5 w-[30px]" />
                    </TableCell>
                    <TableCell className="py-4 pr-6 text-right">
                      <Skeleton className="h-9 w-9 rounded-lg ml-auto" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex-1">
      <div className="flex-1 space-y-6 p-8 pt-6">
        {/* Hero Section */}
        <div className="rounded-2xl overflow-hidden shadow-lg">
          <div
            className="flex"
            style={{
              background: 'linear-gradient(to right, #1e40af, #3b82f6)',
            }}
          >
            <div className="p-8 flex justify-between items-start relative w-full">
              <div className="space-y-3 flex-1">
                <div className="text-white/80 text-sm font-medium">
                  Bilgi Bankası
                </div>
                <h1 className="text-3xl font-bold text-white">
                  Yardım Makaleleri
                </h1>
                <p className="text-white/90 text-sm max-w-xl leading-relaxed">
                  Manager panelindeki yardım widget'ı için içerik yönetimi.
                  Makaleleri düzenleyin, yeni içerikler ekleyin ve kullanıcılarınıza
                  rehberlik sağlayın.
                </p>
              </div>
              <div
                className="w-40 h-40 rounded-lg overflow-hidden relative z-10 flex items-center justify-center ml-8"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }}
              >
                <HelpCircle className="w-20 h-20 text-white" strokeWidth={1.5} />
              </div>
            </div>
          </div>
        </div>

        {/* İstatistikler */}
        <ArticleStats statistics={statistics} />

        {/* Filtreler */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <div className="space-y-4">
            {/* Arama ve Filtre */}
            <div className="flex gap-3 items-center">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  placeholder="Başlık veya içerikte ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 h-11 border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <DropdownMenu open={filterOpen} onOpenChange={setFilterOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className={`h-11 px-5 rounded-lg border-slate-200 hover:bg-slate-50 ${isFilterActive ? "bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100" : ""}`}
                  >
                    <Filter className="mr-2 h-4 w-4" />
                    Filtrele
                    {isFilterActive && (
                      <span className="ml-2 bg-blue-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                        1
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 p-2">
                  <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Makale Durumu
                  </div>
                  <DropdownMenuItem
                    onClick={() => {
                      handleFilterChange('status', filters.status === 'active' ? 'all' : 'active');
                      setFilterOpen(false);
                    }}
                    className={`cursor-pointer py-2.5 px-3 rounded-md ${filters.status === 'active' ? 'bg-green-50' : ''}`}
                  >
                    <div className="flex items-center w-full">
                      <CheckCircle2 className={`mr-3 h-4 w-4 ${filters.status === 'active' ? 'text-green-600' : 'text-slate-400'}`} />
                      <span className={filters.status === 'active' ? 'font-medium text-green-700' : 'text-slate-700'}>Aktif</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      handleFilterChange('status', filters.status === 'inactive' ? 'all' : 'inactive');
                      setFilterOpen(false);
                    }}
                    className={`cursor-pointer py-2.5 px-3 rounded-md ${filters.status === 'inactive' ? 'bg-red-50' : ''}`}
                  >
                    <div className="flex items-center w-full">
                      <XCircle className={`mr-3 h-4 w-4 ${filters.status === 'inactive' ? 'text-red-600' : 'text-slate-400'}`} />
                      <span className={filters.status === 'inactive' ? 'font-medium text-red-700' : 'text-slate-700'}>Pasif</span>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                onClick={() => navigate('/help-articles/new')}
                className="h-11 px-5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
              >
                <Plus className="mr-2 h-4 w-4" />
                Yeni Makale
              </Button>
            </div>

            {/* Aktif filtre belirteçleri */}
            {isFilterActive && (
              <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg border border-slate-100">
                <span className="text-xs font-medium text-slate-600 flex items-center">
                  Aktif Filtreler:
                </span>
                {filters.status === 'active' && (
                  <Badge className="flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-800 border-0 hover:bg-green-200">
                    <CheckCircle2 className="h-3 w-3" />
                    Aktif
                    <X
                      className="h-3.5 w-3.5 ml-1 cursor-pointer hover:text-green-600"
                      onClick={() => handleFilterChange('status', 'all')}
                    />
                  </Badge>
                )}
                {filters.status === 'inactive' && (
                  <Badge className="flex items-center gap-1.5 px-3 py-1 bg-red-100 text-red-800 border-0 hover:bg-red-200">
                    <XCircle className="h-3 w-3" />
                    Pasif
                    <X
                      className="h-3.5 w-3.5 ml-1 cursor-pointer hover:text-red-600"
                      onClick={() => handleFilterChange('status', 'all')}
                    />
                  </Badge>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleFilterChange('status', 'all')}
                  className="h-7 px-3 text-xs text-slate-600 hover:text-slate-800 hover:bg-slate-200"
                >
                  Tümünü Temizle
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex items-center">
          <div className="text-sm text-muted-foreground">
            Toplam <span className="font-semibold text-slate-900">{articles.length}</span> makale bulunuyor
          </div>
        </div>

        {/* Tablo */}
        <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
          <Table className="w-full table-fixed">
            <TableHeader>
              <TableRow className="bg-gradient-to-r from-slate-50 to-slate-100 hover:from-slate-50 hover:to-slate-100 border-b border-slate-200">
                <TableHead className="py-4 pl-6 text-slate-700 font-bold text-sm w-[280px]">Makale Bilgileri</TableHead>
                <TableHead className="py-4 text-slate-700 font-bold text-sm w-[200px]">Kategori</TableHead>
                <TableHead className="py-4 text-slate-700 font-bold text-sm w-[120px]">Durum</TableHead>
                <TableHead className="py-4 text-slate-700 font-bold text-sm w-[80px]">Sıra</TableHead>
                <TableHead className="text-right py-4 pr-6 text-slate-700 font-bold text-sm w-[80px]">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {articles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-24 text-center">
                    <div className="flex flex-col items-center justify-center text-slate-500">
                      <FileText className="h-10 w-10 mb-2 text-slate-400" />
                      <p className="font-medium">Makale bulunamadı</p>
                      <p className="text-sm text-slate-400">Yeni bir makale eklemek için butonu kullanın</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                currentArticles.map((article) => (
                  <TableRow
                    key={article._id}
                    className="border-b border-slate-100 hover:bg-blue-50/30 transition-colors"
                  >
                    {/* Makale Bilgileri */}
                    <TableCell className="py-4 pl-6">
                      <div className="flex flex-col">
                        <span className="font-semibold text-slate-900 break-words text-base">
                          {article.title}
                        </span>
                        {article.shortDescription && (
                          <span className="text-sm text-slate-500 mt-0.5 line-clamp-1">
                            {article.shortDescription}
                          </span>
                        )}
                      </div>
                    </TableCell>

                    {/* Kategori */}
                    <TableCell className="py-4">
                      <span className="text-sm text-slate-700 font-medium">
                        {article.category}
                      </span>
                    </TableCell>

                    {/* Durum */}
                    <TableCell className="py-4">
                      {article.status === 'active' ? (
                        <div className="inline-flex items-center rounded-lg bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-800">
                          <div className="mr-1.5 h-1.5 w-1.5 rounded-full bg-green-600"></div>
                          Aktif
                        </div>
                      ) : (
                        <div className="inline-flex items-center rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                          <div className="mr-1.5 h-1.5 w-1.5 rounded-full bg-slate-400"></div>
                          Pasif
                        </div>
                      )}
                    </TableCell>

                    {/* Sıra */}
                    <TableCell className="py-4">
                      <span className="text-sm text-slate-600 font-medium">
                        {article.order}
                      </span>
                    </TableCell>

                    {/* İşlemler */}
                    <TableCell className="text-right pr-6">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-9 w-9 p-0 rounded-lg hover:bg-slate-100">
                            <span className="sr-only">İşlemler</span>
                            <MoreHorizontal className="h-5 w-5 text-slate-600" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => navigate(`/help-articles/${article._id}`)}>
                            <Edit className="h-4 w-4 mr-2 text-slate-500" />
                            <span>Düzenle</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => openDeleteDialog(article._id!)}
                            className="text-red-600 focus:text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            <span>Sil</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Sayfalama */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <div className="text-sm text-slate-600">
              <span className="font-semibold text-slate-800">{articles.length}</span> makale içinden{' '}
              <span className="font-semibold text-slate-800">
                {(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, articles.length)}
              </span>{' '}
              arası gösteriliyor
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className="h-10 px-4 rounded-lg border-slate-200 hover:bg-slate-50 disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Önceki
              </Button>
              <div className="flex gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    // İlk, son ve mevcut sayfa civarını göster
                    if (page === 1 || page === totalPages) return true;
                    if (Math.abs(page - currentPage) <= 1) return true;
                    return false;
                  })
                  .map((page, index, filteredPages) => (
                    <React.Fragment key={page}>
                      {index > 0 && filteredPages[index - 1] !== page - 1 && (
                        <span className="px-2 text-slate-400">...</span>
                      )}
                      <Button
                        variant="outline"
                        onClick={() => setCurrentPage(page)}
                        className={`h-10 min-w-10 px-3 rounded-lg ${
                          currentPage === page
                            ? "bg-blue-600 hover:bg-blue-700 text-white border-blue-600 pointer-events-none"
                            : "border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        {page}
                      </Button>
                    </React.Fragment>
                  ))}
              </div>
              <Button
                variant="outline"
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className="h-10 px-4 rounded-lg border-slate-200 hover:bg-slate-50 disabled:opacity-50"
              >
                Sonraki
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Makaleyi sil?</AlertDialogTitle>
            <AlertDialogDescription>
              Bu işlem geri alınamaz. Makale kalıcı olarak silinecektir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
